import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Calculator } from 'lucide-react';
import api from '../../services/api';
import '../../styles/InvoiceTab.css';

const InvoiceTab = ({ data, masterData, jobDate, onChange, onTaxChange }) => {
    const [invoiceDetails, setInvoiceDetails] = useState(data || []);
    const [totals, setTotals] = useState({
        provisionForeign: 0,
        provisionHome: 0,
        salesForeign: 0,
        salesHome: 0,
        cost: 0,
        tax: 0
    });

    const revenueTypes = masterData.revenueTypes || [];
    const currencies = masterData.currencies || [];
    const suppliers = masterData.suppliers || [];
    const units = masterData.units || [];

    useEffect(() => {
        setInvoiceDetails(data || []);
    }, [data]);

    useEffect(() => {
        calculateTotals();
    }, [invoiceDetails]);

    const addNewRow = () => {
        const newRow = {
            InvoiceID: null,
            RevenueTypeID: null,
            ProvisionCurrencyID: getBaseCurrency()?.id || null,
            ProvisionExchangeRate: 1,
            ProvisionForeign: 0,
            ProvisionHome: 0,
            ProvisionRate: 0,
            SalesCurrencyID: getBaseCurrency()?.id || null,
            SalesExchangeRate: 1,
            SalesForeign: 0,
            SalesHome: 0,
            SalesRate: 0,
            Cost: 0,
            SupplierID: null,
            RevenueCode: '',
            Quantity: 0,
            UnitID: null,
            TaxID: null,
            TaxPer: 0,
            TaxAmt: 0,
            TaxAmtF: 0,
            AmtInWords: ''
        };
        setInvoiceDetails([...invoiceDetails, newRow]);
    };

    const deleteRow = (index) => {
        if (window.confirm('Delete this row?')) {
            const updated = invoiceDetails.filter((_, i) => i !== index);
            setInvoiceDetails(updated);
            onChange(updated);
        }
    };

    const updateRow = (index, field, value) => {
        const updated = [...invoiceDetails];
        updated[index][field] = value;

        // Auto-calculate on certain field changes
        if (['Quantity', 'ProvisionRate', 'ProvisionExchangeRate', 'ProvisionCurrencyID'].includes(field)) {
            calculateProvision(updated[index]);
        }

        if (['Quantity', 'SalesRate', 'SalesExchangeRate', 'SalesCurrencyID'].includes(field)) {
            calculateSales(updated[index]);
        }

        if (field === 'RevenueTypeID') {
            fetchTaxForRevenue(updated[index], value);
        }

        // Calculate cost
        updated[index].Cost = updated[index].SalesHome - updated[index].ProvisionHome;

        setInvoiceDetails(updated);
        onChange(updated);
    };

    const getBaseCurrency = () => {
        return currencies.find(c => c.StatusBaseCurrency === 1);
    };

    const calculateProvision = (row) => {
        const qty = parseFloat(row.Quantity) || 0;
        const rate = parseFloat(row.ProvisionRate) || 0;
        const exchange = parseFloat(row.ProvisionExchangeRate) || 1;

        if (exchange === 1) {
            // Base currency
            row.ProvisionHome = qty * rate;
            row.ProvisionForeign = 0;
        } else {
            // Foreign currency
            row.ProvisionForeign = qty * rate;
            row.ProvisionHome = row.ProvisionForeign * exchange;
        }

        row.ProvisionHome = parseFloat(row.ProvisionHome.toFixed(2));
        row.ProvisionForeign = parseFloat(row.ProvisionForeign.toFixed(2));
    };

    const calculateSales = (row) => {
        const qty = parseFloat(row.Quantity) || 0;
        const rate = parseFloat(row.SalesRate) || 0;
        const exchange = parseFloat(row.SalesExchangeRate) || 1;

        if (exchange === 1) {
            // Base currency
            row.SalesHome = qty * rate;
            row.SalesForeign = 0;
        } else {
            // Foreign currency
            row.SalesForeign = qty * rate;
            row.SalesHome = row.SalesForeign * exchange;
        }

        row.SalesHome = parseFloat(row.SalesHome.toFixed(2));
        row.SalesForeign = parseFloat(row.SalesForeign.toFixed(2));

        // Calculate tax
        const taxPer = parseFloat(row.TaxPer) || 0;
        row.TaxAmt = (row.SalesHome * taxPer) / 100;
        row.TaxAmtF = exchange !== 1 ? row.TaxAmt / exchange : 0;
        row.TaxAmt = parseFloat(row.TaxAmt.toFixed(2));
        row.TaxAmtF = parseFloat(row.TaxAmtF.toFixed(2));
    };

    const fetchTaxForRevenue = async (row, revenueTypeId) => {
        try {
            const response = await api.get(
                `/TaxAllocation.php?action=get-tax&revenuetypeid=${revenueTypeId}&date=${jobDate}`
            );
            if (response.data.success && response.data.data) {
                row.TaxID = response.data.data.TaxAllocationID;
                row.TaxPer = response.data.data.SGSTP || 0;
                calculateSales(row);
            }
        } catch (error) {
            console.error('Error fetching tax:', error);
        }
    };

    const calculateTotals = () => {
        const newTotals = invoiceDetails.reduce((acc, row) => ({
            provisionForeign: acc.provisionForeign + (parseFloat(row.ProvisionForeign) || 0),
            provisionHome: acc.provisionHome + (parseFloat(row.ProvisionHome) || 0),
            salesForeign: acc.salesForeign + (parseFloat(row.SalesForeign) || 0),
            salesHome: acc.salesHome + (parseFloat(row.SalesHome) || 0),
            cost: acc.cost + (parseFloat(row.Cost) || 0),
            tax: acc.tax + (parseFloat(row.TaxAmt) || 0)
        }), {
            provisionForeign: 0,
            provisionHome: 0,
            salesForeign: 0,
            salesHome: 0,
            cost: 0,
            tax: 0
        });

        setTotals(newTotals);
        
        // Notify parent of tax change
        if (onTaxChange) {
            onTaxChange(newTotals.tax);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="invoice-tab">
            <div className="invoice-toolbar">
                <button className="btn btn-primary btn-sm" onClick={addNewRow}>
                    <Plus size={16} />
                    Add Row
                </button>

                <div className="invoice-summary">
                    <div className="summary-item">
                        <label>Provision:</label>
                        <span>₹{formatCurrency(totals.provisionHome)}</span>
                    </div>
                    <div className="summary-item">
                        <label>Sales:</label>
                        <span>₹{formatCurrency(totals.salesHome)}</span>
                    </div>
                    <div className="summary-item">
                        <label>Cost:</label>
                        <span className={totals.cost >= 0 ? 'profit' : 'loss'}>
                            ₹{formatCurrency(totals.cost)}
                        </span>
                    </div>
                    <div className="summary-item">
                        <label>Tax:</label>
                        <span>₹{formatCurrency(totals.tax)}</span>
                    </div>
                </div>
            </div>

            <div className="invoice-grid-wrapper">
                <table className="invoice-grid">
                    <thead>
                        <tr>
                            <th rowSpan="2">#</th>
                            <th rowSpan="2">Revenue Type</th>
                            <th rowSpan="2">Revenue Code</th>
                            <th rowSpan="2">Supplier</th>
                            <th rowSpan="2">Qty</th>
                            <th rowSpan="2">Unit</th>
                            <th colSpan="4">Provision</th>
                            <th colSpan="4">Sales</th>
                            <th rowSpan="2">Cost</th>
                            <th rowSpan="2">Tax %</th>
                            <th rowSpan="2">Tax Amt</th>
                            <th rowSpan="2">Actions</th>
                        </tr>
                        <tr>
                            <th>Curr</th>
                            <th>Rate</th>
                            <th>Exch</th>
                            <th>Home</th>
                            <th>Curr</th>
                            <th>Rate</th>
                            <th>Exch</th>
                            <th>Home</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoiceDetails.length === 0 ? (
                            <tr>
                                <td colSpan="18" className="empty-row">
                                    No invoice items. Click "Add Row" to add revenue/cost items.
                                </td>
                            </tr>
                        ) : (
                            invoiceDetails.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <select
                                            value={row.RevenueTypeID || ''}
                                            onChange={(e) => updateRow(index, 'RevenueTypeID', parseInt(e.target.value))}
                                            className="grid-select"
                                        >
                                            <option value="">Select</option>
                                            {revenueTypes.map(rt => (
                                                <option key={rt.id} value={rt.id}>{rt.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            value={row.RevenueCode || ''}
                                            onChange={(e) => updateRow(index, 'RevenueCode', e.target.value)}
                                            className="grid-input"
                                        />
                                    </td>
                                    <td>
                                        <select
                                            value={row.SupplierID || ''}
                                            onChange={(e) => updateRow(index, 'SupplierID', parseInt(e.target.value))}
                                            className="grid-select"
                                        >
                                            <option value="">Select</option>
                                            {suppliers.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.Quantity || ''}
                                            onChange={(e) => updateRow(index, 'Quantity', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.001"
                                        />
                                    </td>
                                    <td>
                                        <select
                                            value={row.UnitID || ''}
                                            onChange={(e) => updateRow(index, 'UnitID', parseInt(e.target.value))}
                                            className="grid-select"
                                        >
                                            <option value="">Select</option>
                                            {units.map(u => (
                                                <option key={u.id} value={u.id}>{u.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    
                                    {/* Provision Columns */}
                                    <td>
                                        <select
                                            value={row.ProvisionCurrencyID || ''}
                                            onChange={(e) => updateRow(index, 'ProvisionCurrencyID', parseInt(e.target.value))}
                                            className="grid-select"
                                        >
                                            {currencies.map(c => (
                                                <option key={c.id} value={c.id}>{c.Symbol}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.ProvisionRate || ''}
                                            onChange={(e) => updateRow(index, 'ProvisionRate', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.01"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.ProvisionExchangeRate || ''}
                                            onChange={(e) => updateRow(index, 'ProvisionExchangeRate', parseFloat(e.target.value) || 1)}
                                            className="grid-input number"
                                            step="0.0001"
                                        />
                                    </td>
                                    <td className="amount">{formatCurrency(row.ProvisionHome)}</td>
                                    
                                    {/* Sales Columns */}
                                    <td>
                                        <select
                                            value={row.SalesCurrencyID || ''}
                                            onChange={(e) => updateRow(index, 'SalesCurrencyID', parseInt(e.target.value))}
                                            className="grid-select"
                                        >
                                            {currencies.map(c => (
                                                <option key={c.id} value={c.id}>{c.Symbol}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.SalesRate || ''}
                                            onChange={(e) => updateRow(index, 'SalesRate', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.01"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.SalesExchangeRate || ''}
                                            onChange={(e) => updateRow(index, 'SalesExchangeRate', parseFloat(e.target.value) || 1)}
                                            className="grid-input number"
                                            step="0.0001"
                                        />
                                    </td>
                                    <td className="amount">{formatCurrency(row.SalesHome)}</td>
                                    
                                    <td className={`amount ${row.Cost >= 0 ? 'profit' : 'loss'}`}>
                                        {formatCurrency(row.Cost)}
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={row.TaxPer || ''}
                                            onChange={(e) => updateRow(index, 'TaxPer', parseFloat(e.target.value) || 0)}
                                            className="grid-input number"
                                            step="0.01"
                                        />
                                    </td>
                                    <td className="amount">{formatCurrency(row.TaxAmt)}</td>
                                    <td>
                                        <button
                                            className="btn-icon btn-danger"
                                            onClick={() => deleteRow(index)}
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colSpan="9">Total:</th>
                            <th className="amount">{formatCurrency(totals.provisionHome)}</th>
                            <th colSpan="3"></th>
                            <th className="amount">{formatCurrency(totals.salesHome)}</th>
                            <th className={`amount ${totals.cost >= 0 ? 'profit' : 'loss'}`}>
                                {formatCurrency(totals.cost)}
                            </th>
                            <th></th>
                            <th className="amount">{formatCurrency(totals.tax)}</th>
                            <th></th>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="invoice-footer">
                <div className="invoice-totals-card">
                    <h4>Summary</h4>
                    <div className="totals-grid">
                        <div className="total-row">
                            <span>Subtotal (Sales):</span>
                            <strong>{formatCurrency(totals.salesHome)}</strong>
                        </div>
                        <div className="total-row">
                            <span>Tax:</span>
                            <strong>{formatCurrency(totals.tax)}</strong>
                        </div>
                        <div className="total-row grand-total">
                            <span>Grand Total:</span>
                            <strong>{formatCurrency(totals.salesHome + totals.tax)}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceTab;